import React from 'react';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { AnimatedLabel } from './AnimatedLabel';
import { IconSelector, IconCheck } from '@tabler/icons-react';

interface SelectOption {
  /** Value stored when selected. */
  value: string;
  /** Display label. */
  label: string;
  /**
   * Optional leading glyph (e.g. a provider logo) rendered before the label in
   * the option row and in the closed button when selected. Wrapped aria-hidden
   * so accessible names stay label-only.
   */
  icon?: React.ReactNode;
}

interface SelectProps {
  /** Accessible name when the visible row already supplies context. */
  ariaLabel?: string;
  /** Optional layout class for compact inline controls. */
  className?: string;
  /** Smaller field chrome for inline authoring. */
  compact?: boolean;
  /** Optional closed-button content; full option labels remain in the picker. */
  selectedContent?: React.ReactNode;
  /** Currently selected value (empty string for none). */
  value: string;
  /** Called when the selection changes. */
  onChange: (value: string) => void;
  /** Available options. */
  options: SelectOption[];
  /** Placeholder text when nothing is selected. */
  placeholder?: string;
  /** Field label rendered above the select. */
  label?: string;
  /** Help text rendered below the select. */
  helpText?: string;
  /** When true, the select is visually dimmed and non-interactive. */
  disabled?: boolean;
  /** Optional node rendered inline after the label (e.g., a feature gate badge). */
  labelSuffix?: React.ReactNode;
}

/**
 * Accessible select (dropdown) built on Headless UI Listbox.
 * Provides keyboard navigation, screen-reader support, and custom styling.
 */
export const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  label,
  helpText,
  disabled = false,
  labelSuffix,
  ariaLabel, className, compact = false, selectedContent,
}) => {
  const selected = options.find((o) => o.value === value);

  return (
    <div className={className}>
      {label && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm text-muted-foreground uppercase tracking-wider font-medium">
            {label}
          </span>
          {labelSuffix}
        </div>
      )}

      <Listbox value={value} onChange={onChange} disabled={disabled}>
        <div className="relative">
          <ListboxButton
            aria-label={ariaLabel ?? (label ? `${label}: ${selected?.label ?? placeholder}` : undefined)}
            className={`flex ${compact ? 'min-h-9 bg-surface-raised px-2.5 py-1.5 border-transparent' : 'min-h-11 bg-background px-3 py-2 border-border'} w-full min-w-0 touch-manipulation items-center justify-between gap-2 rounded-lg border text-left text-sm transition-colors focus:outline-none focus-visible:border-volt focus-visible:ring-2 focus-visible:ring-volt ${
              disabled ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          >
            <span
              className={`flex min-w-0 items-center gap-2 ${selected ? 'text-foreground' : 'text-subtle-foreground'}`}
            >
              {selected?.icon && (
                <span aria-hidden="true" className="shrink-0">
                  {selected.icon}
                </span>
              )}
              {selectedContent !== undefined ? selectedContent
                : compact ? <AnimatedLabel text={selected ? selected.label : placeholder} truncate className="min-w-0" /> : selected ? selected.label : placeholder}
            </span>
            <IconSelector aria-hidden="true" className="w-4 h-4 shrink-0 text-muted-foreground" />
          </ListboxButton>

          <ListboxOptions transition
            anchor={{ to: 'bottom start', gap: 4, padding: 8 }}
            className={`origin-top transition duration-200 data-closed:opacity-0 data-closed:-translate-y-1 data-closed:scale-[0.98] data-leave:duration-150 motion-reduce:transition-none ${compact ? 'z-[100] min-w-[var(--button-width)] max-w-[calc(100vw-1rem)] w-max' : 'z-30 w-[var(--button-width)]'} [--anchor-max-height:15rem] overflow-auto bg-surface-raised border border-border rounded-lg focus:outline-none`}
          >
            {options.map((option) => (
              <ListboxOption
                key={option.value}
                value={option.value}
                className={({ active, selected: sel }) =>
                  `relative min-h-11 cursor-pointer select-none px-3 py-2 text-sm ${
                    active ? 'bg-volt/20 text-foreground' : 'text-muted-foreground'
                  } ${sel ? 'font-medium' : ''}`
                }
              >
                {({ selected: sel }) => (
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      {option.icon && (
                        <span aria-hidden="true" className="shrink-0">
                          {option.icon}
                        </span>
                      )}
                      <span>{option.label}</span>
                    </span>
                    {sel && <IconCheck className="w-4 h-4 text-volt-soft" />}
                  </div>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>

      {helpText && (
        <p className="text-xs text-subtle-foreground mt-1">{helpText}</p>
      )}
    </div>
  );
};
